// ABOUTME: Reads the race countdown again after a save that can move a race — an event created, edited, moved or
// ABOUTME: deleted, a plan laid on — from whatever screen, not waiting for a realtime echo.
import { ApolloLink } from "@apollo/client"
import { Observable, getMainDefinition } from "@apollo/client/utilities"
import type { DocumentNode } from "graphql"
import {
  CalendarItemCreateEventDocument,
  CalendarItemDeleteDocument,
  CalendarItemUpdateDocument,
  CalendarItemUpdateEventDocument,
  PlanApplyDocument,
} from "@/graphql/main/gql/graphql"
import { refreshStartsIfTouched } from "@/model/competitions/startsRefresh"
import { isMutationOperation } from "./operationKind"

const operationNameOf = (document: DocumentNode): string | undefined => getMainDefinition(document).name?.value

/**
 * The saves that can create, move, change or remove an event in a calendar — named through their documents, so a
 * renamed operation cannot quietly fall out of the list. A workout, a rest or a menu is no race: the athlete's
 * countdown counts race events only (nextRaces). Nor is a goal saved in the profile, since WA-829: the season's races
 * are calendar events, and nextRaces reads no goal.
 */
export const MAY_MOVE_A_RACE: ReadonlySet<string | undefined> = new Set(
  [
    CalendarItemCreateEventDocument,
    CalendarItemUpdateEventDocument,
    CalendarItemUpdateDocument,
    CalendarItemDeleteDocument,
    PlanApplyDocument,
  ].map(operationNameOf),
)

/**
 * The countdown's query is its own (useNextRace), and these saves reach the calendar by many paths — the event form,
 * a drag, a paste, an undoable delete, a plan — most of them refreshing only the weeks. The realtime echo reaches the
 * countdown too, but only while the channel holds. The answer is the one place every such save passes, and it reads
 * every reader on screen (refreshStartsIfTouched(null)); with none, there is no request (startsRefresh). The coach's
 * list has one: each such save costs one read of every athlete's next race (nextRaces, useAthletesUpcomingStarts). A
 * countdown — an athlete's calendar, or a coach's of one athlete — costs one read of that athlete's next races, a
 * workout's drag included (calendarItemUpdate moves any card); a coach's calendar of several athletes has a reader a
 * chunk of ten rows on screen (TrainingsMultiAthlethes), so one read a chunk; the season races tab reads a year of
 * starts. The reads go in line — a run at a time, its readers side by side — and queued ones merge.
 */
export function createStartsRefreshLink(): ApolloLink {
  return new ApolloLink((operation, forward) => {
    if (!isMutationOperation(operation.query) || !MAY_MOVE_A_RACE.has(operation.operationName)) {
      return forward(operation)
    }
    return new Observable((observer) => {
      const subscription = forward(operation).subscribe({
        // Any answer with data, errors or not: a plan laid on for several athletes can land for some of them. A read
        // too many costs one request; one too few leaves the countdown stale until the poll.
        next: (result) => {
          observer.next(result)
          if (result.data) void refreshStartsIfTouched(null)
        },
        error: (error: unknown) => observer.error(error),
        complete: () => observer.complete(),
      })
      return () => subscription.unsubscribe()
    })
  })
}
